import "@/App.css";
import { motion } from "framer-motion";
import { Instagram } from "lucide-react";

// Brand Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_7d248b78-eebd-44e5-a49e-5acabed393a7/artifacts/3kpfejdk_Eternite%20Logo.png";
const HERO_IMAGE = "https://images.unsplash.com/photo-1764002317052-f0e1c4263789?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxhcnRpc2FuYWwlMjBjb2ZmZWUlMjBwb3VyaW5nfGVufDB8fHx8MTc3NDIxMTQwM3ww&ixlib=rb-4.1.0&q=85";
const ABOUT_IMAGE_1 = "https://images.unsplash.com/photo-1615174111727-b165bfe654a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwY29mZmVlJTIwY3VwfGVufDB8fHx8MTc3NDIxMTQwNHww&ixlib=rb-4.1.0&q=85";
const ABOUT_IMAGE_2 = "https://images.pexels.com/photos/4815951/pexels-photo-4815951.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};

// Noise Overlay Component
const NoiseOverlay = () => (
  <div className="noise-overlay" aria-hidden="true" />
);

// Navigation Component
const Navigation = () => (
  <motion.nav 
    initial={{ opacity: 0, y: -20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    className="fixed top-0 left-0 right-0 z-40 px-6 md:px-12 py-6"
  >
    <div className="max-w-7xl mx-auto flex justify-between items-center">
      <img 
        src={LOGO_URL} 
        alt="Eternite Coffee" 
        className="h-8 md:h-10"
        data-testid="nav-logo"
      />
      
    </div>
  </motion.nav>
);

// Hero Section Component
const HeroSection = () => (
  <section className="min-h-screen relative flex items-center px-6 md:px-12 pt-24 pb-12">
    <div className="max-w-7xl mx-auto w-full">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        {/* Text Content */}
        <motion.div 
          className="lg:col-span-6 xl:col-span-5"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.p 
            variants={fadeInUp}
            className="font-body text-xs uppercase tracking-[0.2em] text-charcoal/60 mb-4"
          >
            Kommer Snart
          </motion.p>
          <motion.h1 
            variants={fadeInUp}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-medium tracking-tight leading-none text-charcoal mb-6"
            data-testid="hero-title"
          >
            Kaffe,
            <br />
            <span className="text-terracotta">Nytänkt</span>
          </motion.h1>
          <motion.p 
            variants={fadeInUp}
            className="font-body text-base md:text-lg text-charcoal/70 leading-relaxed max-w-md mb-8"
            data-testid="hero-description"
          >
            Ett nytt kapitel inom hantverkskaffe. Omsorgsfullt utvalt, 
            noggrant rostat, evigt minnesvärt.
          </motion.p>
          
        </motion.div>

        {/* Hero Image */}
        <motion.div 
          className="lg:col-span-6 xl:col-span-7 relative"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
        >
          <div className="relative">
            <img 
              src={HERO_IMAGE} 
              alt="Hantverkskaffe hälls"
              className="w-full h-[400px] md:h-[500px] lg:h-[600px] object-cover rounded-2xl image-hover shadow-[0_8px_32px_rgba(74,68,83,0.12)]"
              data-testid="hero-image"
            />
            {/* Floating accent */}
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-sage/30 rounded-2xl -z-10" />
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// About Section Component
const AboutSection = () => (
  <section id="about" className="py-24 md:py-32 px-6 md:px-12">
    <div className="max-w-7xl mx-auto">
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        {/* Main Image - Large */}
        <motion.div 
          className="md:col-span-8 md:row-span-2"
          variants={fadeInUp}
        >
          <img 
            src={ABOUT_IMAGE_2}
            alt="Premiumkaffebönor"
            className="w-full h-[300px] md:h-full object-cover rounded-2xl image-hover shadow-[0_8px_32px_rgba(74,68,83,0.12)]"
            data-testid="about-main-image"
          />
        </motion.div>

        {/* Text Block */}
        <motion.div 
          className="md:col-span-4"
          variants={fadeInUp}
        >
          <p className="font-body text-xs uppercase tracking-[0.2em] text-charcoal/60 mb-4">
            Vår Historia
          </p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-tight text-charcoal mb-4" data-testid="about-title">
            Skapat med
            <br />
            <span className="text-terracotta">Omsorg</span>
          </h2>
          <p className="font-body text-charcoal/70 leading-relaxed" data-testid="about-description">
            Eternite är född ur en passion för exceptionellt kaffe. Vi samarbetar 
            direkt med odlare som delar vårt engagemang för kvalitet och 
            hållbarhet, och ger dig bönor som berättar en historia i varje klunk.
          </p>
        </motion.div>

        {/* Supporting Image */}
        <motion.div 
          className="md:col-span-4"
          variants={fadeInUp}
        >
          <img 
            src={ABOUT_IMAGE_1}
            alt="Minimalistisk kaffekopp"
            className="w-full h-[250px] md:h-[280px] object-cover rounded-2xl image-hover shadow-[0_8px_32px_rgba(74,68,83,0.12)]"
            data-testid="about-secondary-image"
          />
        </motion.div>
      </motion.div>

      {/* Values Grid */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 md:mt-24"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        {[
          { title: "Utvalt", desc: "Direkta partnerskap med odlare på tre kontinenter" },
          { title: "Rostat", desc: "Småskalig rostning för att låsa upp varje bönas potential" },
          { title: "Levererat", desc: "Färskt hem till dig inom dagar efter rostning" }
        ].map((item, index) => (
          <motion.div 
            key={item.title}
            variants={fadeInUp}
            className="border-t border-charcoal/20 pt-6"
            data-testid={`value-card-${index}`}
          >
            <h3 className="font-heading text-2xl md:text-3xl font-medium text-charcoal mb-2">
              {item.title}
            </h3>
            <p className="font-body text-charcoal/60 text-sm leading-relaxed">
              {item.desc}
            </p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

// Footer Component
const Footer = () => (
  <footer className="py-12 px-6 md:px-12 border-t border-charcoal/10">
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-center gap-8">
        <div className="flex flex-col items-center md:items-start gap-4">
          <img 
            src={LOGO_URL} 
            alt="Eternite Coffee" 
            className="h-6"
            data-testid="footer-logo"
          />
          <p className="font-body text-sm text-charcoal/50">
            &copy; {new Date().getFullYear()} Eternite. Alla rättigheter förbehållna.
          </p>
        </div>
        
        <div className="flex items-center gap-6">
          <a 
            href="https://instagram.com/la_Case_bc" 
            className="text-charcoal/50 hover:text-terracotta transition-colors duration-300"
            aria-label="Instagram"
            data-testid="social-instagram"
          >
            <Instagram size={20} />
          </a>
          
        </div>
      </div>
    </div>
  </footer>
);

// Main App Component
function App() {
  return (
    <div className="bg-cream min-h-screen">
      <NoiseOverlay />
      <Navigation />
      <main>
        <HeroSection />
        <AboutSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
